import { RACI_COLUMNS, criticalDateTone, getProjectDemoSignals } from './projectDemoSignals.js';

function formatDateLabel(date) {
  if (!date) return 'TBD';
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

function stakeholderInitials(name) {
  return String(name || 'Stakeholder')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export function ProjectStakeholderReadiness({ project }) {
  const signals = getProjectDemoSignals(project);
  const people = signals.stakeholders.filter((s) => s.type === 'person').length;
  const organizations = signals.stakeholders.length - people;

  return (
    <div className="profile-tab stakeholder-readiness" data-testid="project-stakeholder-readiness">
      <div className="stakeholder-hero">
        <div>
          <div className="eyebrow">Stakeholders, RACI, and key dates</div>
          <h3>{signals.projectName} responsibility map</h3>
          <p className="muted">
            Product ownership, approval paths, support organizations, and time-sensitive resource
            events are visible together for the demo path.
          </p>
        </div>
        <div className="stakeholder-hero-stats" aria-label="Stakeholder summary">
          <span>
            <strong>{people}</strong>
            people
          </span>
          <span>
            <strong>{organizations}</strong>
            orgs
          </span>
          <span>
            <strong>{signals.criticalDates.length}</strong>
            key dates
          </span>
        </div>
      </div>

      <div className="bento stakeholder-bento">
        <section className="span-7 card stakeholder-map-card">
          <header className="row spread">
            <div>
              <div className="eyebrow">Stakeholder map</div>
              <h2 className="card-title">{signals.stakeholders.length} people and organizations</h2>
            </div>
            <span className="chip outline sm">View only</span>
          </header>
          <div className="stakeholder-map-grid" aria-label="Stakeholder map">
            {signals.stakeholders.map((stakeholder) => (
              <article key={stakeholder.id} className={`stakeholder-node ${stakeholder.type}`}>
                <span className="stakeholder-node-mark" aria-hidden>
                  {stakeholder.type === 'organization'
                    ? 'ORG'
                    : stakeholderInitials(stakeholder.name)}
                </span>
                <div>
                  <strong>{stakeholder.name}</strong>
                  <p>{stakeholder.role}</p>
                  <div className="row stakeholder-node-meta">
                    <span className="chip outline sm">{stakeholder.domain}</span>
                    <span className="muted small">{stakeholder.organization}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="span-5 card critical-dates-card" data-testid="project-critical-dates">
          <header>
            <div className="eyebrow">Critical dates</div>
            <h2 className="card-title">Product support clock</h2>
          </header>
          <ul className="critical-date-list">
            {signals.criticalDates.map((item) => (
              <li
                key={item.id}
                className={`critical-date-item tone-${criticalDateTone(item.status)}`}
              >
                <time dateTime={item.date}>{formatDateLabel(item.date)}</time>
                <div>
                  <strong>{item.label}</strong>
                  <p className="muted small">
                    {item.owner} · {item.source}
                  </p>
                </div>
                <span className={`chip ${criticalDateTone(item.status)} sm`}>{item.status}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="span-12 card raci-card" data-testid="project-raci-matrix">
          <header className="row spread">
            <div>
              <div className="eyebrow">RACI matrix</div>
              <h2 className="card-title">Responsibility by product participant</h2>
            </div>
          </header>
          <div className="raci-legend" aria-label="RACI legend">
            {RACI_COLUMNS.map((column) => (
              <span key={column.key}>
                <strong>{column.key}</strong>
                {column.label}
              </span>
            ))}
          </div>
          <div className="raci-table-wrap">
            <table className="table raci-table">
              <thead>
                <tr>
                  <th>Stakeholder</th>
                  <th>Type</th>
                  <th>Domain</th>
                  {RACI_COLUMNS.map((column) => (
                    <th key={column.key}>{column.key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {signals.stakeholders.map((stakeholder) => (
                  <tr key={stakeholder.id}>
                    <td>
                      <strong>{stakeholder.name}</strong>
                      <div className="muted small">{stakeholder.organization}</div>
                    </td>
                    <td>{stakeholder.type === 'organization' ? 'Organization' : 'Person'}</td>
                    <td>{stakeholder.domain}</td>
                    {RACI_COLUMNS.map((column) => {
                      const active = stakeholder.raci.includes(column.key);
                      return (
                        <td
                          key={column.key}
                          className={active ? 'raci-active' : 'raci-empty'}
                          aria-label={active ? column.label : `Not ${column.label}`}
                          title={column.label}
                        >
                          {active ? column.key : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
